import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { FundingNeeds, Needs } from 'app/models/dto/funding-needs.model';
import { FundingNeedsService } from 'app/services/funding-needs/funding-needs.service';
import { catchError, of, tap } from 'rxjs';
import {
  ClearFundingNeedsStore,
  CreateFundingNeeds,
  DeleteFundingNeeds,
  GetFundingNeeds,
  UpdateFundingNeeds
} from './funding-needs.actions';

export class FundingNeedsStateModel {
    fundingNeeds:  FundingNeeds | null;
}

@State<FundingNeedsStateModel>({
    name: 'fundingNeeds',
    defaults: {
        fundingNeeds: null
    }
})

@Injectable()

export class FundingNeedsState {

    @Selector()
    static fundingNeeds(state: FundingNeedsStateModel) {
        return state.fundingNeeds;
    }

    private _version = 'v1';

    constructor(
        private fundingNeedsService: FundingNeedsService
    ) {}

    @Action(GetFundingNeeds)
    getFundingNeeds({ patchState }: StateContext<FundingNeedsStateModel>, { borrowerId }: GetFundingNeeds) {
        return this.fundingNeedsService.get(borrowerId).pipe(
            catchError(err => {
                return of();
            }),
            tap(fundingNeeds => {
                if (!!fundingNeeds?.needs && fundingNeeds.needs.length > 0) {
                    const modifiedNeeds = fundingNeeds.needs.map((need: any) => {
                        return this.keysToCamel(need);
                    });
                    fundingNeeds.needs.splice(0, fundingNeeds.needs.length, ...modifiedNeeds);
                }
                patchState({ fundingNeeds });
            })
        );
    }

    @Action(CreateFundingNeeds)
    createFundingNeeds({ patchState }: StateContext<FundingNeedsStateModel>, { borrowerId,  fundingNeeds }: CreateFundingNeeds) {
        fundingNeeds.version = this._version;
        const modifiedNeeds = fundingNeeds.needs.map((need: any) => {
            return this.keysToKebab(need);
        });
        fundingNeeds.needs.splice(0, fundingNeeds.needs.length, ...modifiedNeeds);
        return this.fundingNeedsService.post(borrowerId, fundingNeeds).pipe(
            catchError(err => {
                return of();
            }),
            tap(newFundingNeeds => {
                const modifiedResponse = newFundingNeeds.needs.map((need: any) => {
                    return this.keysToCamel(need);
                });
                newFundingNeeds.needs.splice(0, newFundingNeeds.needs.length, ...modifiedResponse);
                patchState({ fundingNeeds: newFundingNeeds });
            })
        );
    }

    @Action(UpdateFundingNeeds)
    updateFundingNeeds({ patchState }: StateContext<FundingNeedsStateModel>,
    { borrowerId, fundingNeedsId, fundingNeeds }: UpdateFundingNeeds) {
        fundingNeeds.version = this._version;
        const modifiedNeeds = fundingNeeds.needs.map((need: any) => {
            return this.keysToKebab(need);
        });
        fundingNeeds.needs.splice(0, fundingNeeds.needs.length, ...modifiedNeeds);
        return this.fundingNeedsService.put(borrowerId, fundingNeedsId, fundingNeeds).pipe(
            catchError(err => {
                return of();
            }),
            tap(updatedFundingNeeds => {
                const modifiedResponse = updatedFundingNeeds.needs.map((need: any) => {
                    return this.keysToCamel(need);
                });
                updatedFundingNeeds.needs.splice(0, updatedFundingNeeds.needs.length, ...modifiedResponse);
                patchState({ fundingNeeds: updatedFundingNeeds });
            })
        );
    }

    @Action(DeleteFundingNeeds)
    deleteAutomationSequence({ patchState }: StateContext<FundingNeedsStateModel>,
        { borrowerId, fundingNeedsId }: DeleteFundingNeeds) {
        return this.fundingNeedsService.delete(borrowerId, fundingNeedsId).pipe(
            catchError(err => {
                return of();
            }), tap((response) => {
                if (response.id === fundingNeedsId) {
                    patchState({ fundingNeeds: null });
                }
            })
        );
    }

    @Action(ClearFundingNeedsStore)
    clearFundingNeedsStore(
      { setState }: StateContext<FundingNeedsStateModel>,
      { }: ClearFundingNeedsStore
    ) {
      setState({
        fundingNeeds: null
      });
    }

    /**
     * Converts Needs keys from kabab-case to camelCase
     *
     * @param need
     * @returns any | Needs
     */
    private keysToCamel(need: any) {
        if (need === Object(need)) {
            const modifiedNeeds = {};
            Object.keys(need)
                .forEach((key) => {
                    modifiedNeeds[this.toCamel(key)] = this.keysToCamel(need[key]);
                });
            return modifiedNeeds as Needs;
        } else {
            return need as any;
        }
    }

    private toCamel(kebab: string) {
        return kebab.replace(/([-_][a-z])/ig, ($1) => {
            return $1.toUpperCase()
                .replace('-', '')
                .replace('_', '');
        });
    }

    /**
     * Converts Needs keys from camelCase to kabab-case
     *
     * @param need
     * @returns any | Needs
     */
    private keysToKebab(need: any) {
        if (need === Object(need)) {
            const modifiedNeeds = {};
            Object.keys(need)
                .forEach((key) => {
                    modifiedNeeds[this.toKebab(key)] = this.keysToKebab(need[key]);
                });
            return modifiedNeeds as Needs;
        } else {
            return need as any;
        }
    }

    private toKebab(camel: string) {
        return camel.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
    }

}
